import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { from, map, mergeMap, switchMap, throwError, toArray } from "rxjs";

import { BaseService } from "../../@core/services/base.service";
import { environment } from "../../../environments/environment.dev";
import { IFile } from "../../@core/models/model";
import { KNOWLEDGE_SCOPE } from "../models/contract-management.model";
import { createExternalRequestContext } from "../../@core/utils/http-context.utils";

/** A presigned slot to PUT one file into. */
export interface IPresignedUpload {
    fileId: string;
    fileName: string;
    uploadUrl: string;
    s3Key: string;
}

/** A file already uploaded to S3, ready to register with the API. */
export interface IS3UploadRef {
    fileId: string;
    s3Key: string;
    fileName: string;
    contentType: string;
    size: number;
}

/** Files PUT to S3 at once. */
const S3_UPLOAD_CONCURRENCY = 3;

@Injectable({ providedIn: 'root' })
export class RepositoryService extends BaseService {

    constructor(
        protected override http: HttpClient
    ) {
        super(http);
    }

    apiUrl = environment.apiUrl + '/repository';
    parentFolderId: string | null = null

    getParentFolderId() {
        return this.parentFolderId;
    }

    getFiles(params?: { project?: string; scope?: KNOWLEDGE_SCOPE; type?: KNOWLEDGE_SCOPE }) {
        return this.get<IFile[]>(this.apiUrl, params)
    }

    getFolderDetails(folderId: string) {
        return this.get<(IFile & { childs: IFile[] })>(this.apiUrl + '/folder/' + folderId)
    }

    getDocTypeFiles(docTypeId: string, filters: { search: string, skip: number, limit: number, reqOnlyFiles?: boolean }) {
        return this.post<{
            files: IFile[],
            total: number
        }>(this.apiUrl + '/get-doc-type-files', { docTypeId, filters })
    }

    getAnyDocTypeFiles(filters: { search: string, skip: number, limit: number, reqOnlyFiles?: boolean, scope?: string, project?: string }) {
        return this.post<{
            files: IFile[],
            total: number
        }>(this.apiUrl + '/get-doc-type-files', { filters })
    }

    uploadFiles(formData: FormData) {
        return this.post<any>(this.apiUrl + '/upload-file', formData)
    }

    /**
     * Upload files straight to S3 with presigned PUT URLs, then hand back the refs
     * to register with the API.
     *
     * Posting the bytes through the API instead meant one multipart request holding
     * every file in server memory, which fell over on batches of large files.
     * `progress` reports how many files have landed so callers can show it.
     */
    uploadFilesToS3(files: File[], progress?: (done: number, total: number) => void) {
        const payload = files.map(file => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
        }));

        return this.post<{ urls: IPresignedUpload[] }>(this.apiUrl + '/upload-presigned-urls', { files: payload }).pipe(
            switchMap(res => {
                const urls = res?.data?.urls;
                if (!res?.success || !urls?.length) {
                    return throwError(() => new Error('Failed to get upload URLs'));
                }

                let done = 0;
                progress?.(0, urls.length);

                // Bounded concurrency: a wide fan-out of large uploads starves the
                // connection pool and makes every file slower.
                return from(urls.map((presigned, index) => ({ presigned, file: files[index] }))).pipe(
                    // Paired by index, not by name: two picked files can share a name.
                    mergeMap(({ presigned, file }) => {
                        if (!file) return throwError(() => new Error(`Missing file for ${presigned.fileName}`));
                        return this.uploadToS3(presigned.uploadUrl, file).pipe(
                            map((): IS3UploadRef => {
                                progress?.(++done, urls.length);
                                return {
                                    fileId: presigned.fileId,
                                    s3Key: presigned.s3Key,
                                    fileName: presigned.fileName,
                                    contentType: file.type || 'application/octet-stream',
                                    size: file.size,
                                };
                            })
                        );
                    }, S3_UPLOAD_CONCURRENCY),
                    toArray()
                );
            })
        );
    }

    /** PUT a single file to S3. Marked external so interceptors leave the signature alone. */
    uploadToS3(presignedUrl: string, file: File) {
        return this.http.put(presignedUrl, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            context: createExternalRequestContext(),
        });
    }

    /** Register files already in S3. Processing continues in the background. */
    registerUploads(payload: { files: IS3UploadRef[], parentFolderId?: string, knowledgeBaseStoreId?: string, scope?: string, project?: string }) {
        return this.post<null>(this.apiUrl + '/register-uploads', payload)
    }

    deleteFile(fileIds: string | string[]) {
        return this.delete<IFile>(this.apiUrl + '/file/', { fileIds, parentFolderId: this.parentFolderId })
    }

    downloadRepoFiles(fileIds: IFile['_id'][]) {
        return this.post<{ data: Buffer }>(this.apiUrl + '/download-repo-files', { fileIds })
    }

    linkToKnowledgeBase(selectedKnowledgeBases: string[], selectedFiles?: string[], contractIds?: string[], projectIds?: string[]) {
        return this.post<any>(this.apiUrl + '/link-to-knowledge-base', {
            knowledgeBaseIds: selectedKnowledgeBases,
            ...(selectedFiles && selectedFiles.length > 0 && { fileIds: selectedFiles }),
            ...(contractIds && contractIds.length > 0 && { contractIds: contractIds }),
            ...(projectIds && projectIds.length > 0 && { projectIds: projectIds })
        })
    }
}
